<template>
  <div class="user-management">
    <!-- Header -->
    <div class="header-section">
      <h2>👥 User Management</h2>
      <p class="description">
        Manage users and access permissions for your organization. 
        Add new team members, update roles, and control access to features.
      </p>
    </div>

    <!-- Management Controls -->
    <div class="management-section primary-section">
      <div class="controls-header">
        <div class="section-info">
          <h3><i class="fas fa-users-cog"></i> Organization Users</h3>
          <p class="section-description">{{ users.length }} active users • {{ organization?.name }}</p>
        </div>
        <div class="action-buttons">
          <button 
            @click="showInviteModal = true" 
            class="btn btn-primary"
            :disabled="!canInviteUsers"
          >
            <i class="fas fa-user-plus"></i> Invite User
          </button>
          <button @click="loadUsers" class="btn btn-outline" :disabled="loading">
            <i class="fas fa-sync"></i> {{ loading ? 'Loading...' : 'Refresh' }}
          </button>
        </div>
      </div>

      <!-- Search and Filters -->
      <div class="filters-section">
        <div class="search-group">
          <input 
            v-model="searchQuery" 
            placeholder="Search users by name, email, or role..."
            class="search-input"
          >
          <i class="fas fa-search search-icon"></i>
        </div>
        <select v-model="roleFilter" class="filter-select">
          <option value="">All Roles</option>
          <option v-for="role in availableRoles" :key="role.value" :value="role.value">
            {{ role.label }}
          </option>
        </select>
        <select v-model="statusFilter" class="filter-select">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <!-- Users Table -->
      <div class="users-table-container">
        <div v-if="loading" class="loading-state">
          <i class="fas fa-spinner fa-spin"></i>
          <p>Loading users...</p>
        </div>

        <div v-else-if="filteredUsers.length === 0" class="empty-state">
          <i class="fas fa-users"></i>
          <h3>No Users Found</h3>
          <p v-if="searchQuery || roleFilter || statusFilter">
            Try adjusting your search criteria or filters.
          </p>
          <p v-else>
            Get started by inviting team members to your organization.
          </p>
          <button 
            v-if="canInviteUsers && !searchQuery && !roleFilter && !statusFilter" 
            @click="showInviteModal = true" 
            class="btn btn-primary"
          >
            <i class="fas fa-user-plus"></i> Invite First User
          </button>
        </div>

        <div v-else class="users-table">
          <table class="modern-table">
            <thead>
              <tr>
                <th @click="sortBy('first_name')" class="sortable">
                  Name <i :class="getSortIcon('first_name')"></i>
                </th>
                <th @click="sortBy('email')" class="sortable">
                  Email <i :class="getSortIcon('email')"></i>
                </th>
                <th @click="sortBy('role')" class="sortable">
                  Role <i :class="getSortIcon('role')"></i>
                </th>
                <th>Department</th>
                <th @click="sortBy('last_active_at')" class="sortable">
                  Last Active <i :class="getSortIcon('last_active_at')"></i>
                </th>
                <th @click="sortBy('is_active')" class="sortable">
                  Status <i :class="getSortIcon('is_active')"></i>
                </th>
                <th width="120">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in paginatedUsers" :key="user.id" class="user-row">
                <td>
                  <div class="user-info">
                    <div class="user-avatar">
                      {{ getInitials(user.first_name, user.last_name) }}
                    </div>
                    <div>
                      <div class="user-name">{{ user.first_name }} {{ user.last_name }}</div>
                      <div class="user-title">{{ user.title || 'No title' }}</div>
                    </div>
                  </div>
                </td>
                <td>{{ user.email }}</td>
                <td>
                  <span 
                    class="role-badge" 
                    :class="getRoleClass(user.role)"
                    :title="getRoleDescription(user.role)"
                  >
                    {{ getRoleLabel(user.role) }}
                  </span>
                </td>
                <td>{{ user.department || '-' }}</td>
                <td>{{ formatDate(user.last_active_at) }}</td>
                <td>
                  <span class="status-badge" :class="user.is_active ? 'active' : 'inactive'">
                    {{ user.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td>
                  <div class="action-buttons">
                    <button 
                      @click="editUser(user)" 
                      class="btn-icon" 
                      title="Edit User"
                      :disabled="!canEditUser()"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button 
                      @click="toggleUserStatus(user)" 
                      class="btn-icon" 
                      :title="user.is_active ? 'Deactivate User' : 'Activate User'"
                      :disabled="!canEditUser()"
                    >
                      <i :class="user.is_active ? 'fas fa-user-slash' : 'fas fa-user-check'"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="pagination-controls">
          <div class="pagination-info">
            Showing {{ ((currentPage - 1) * pageSize) + 1 }} to {{ Math.min(currentPage * pageSize, filteredUsers.length) }} of {{ filteredUsers.length }} users
          </div>
          <div class="pagination-buttons">
            <button 
              @click="goToPage(1)" 
              class="btn-pagination" 
              :disabled="currentPage === 1"
            >
              <i class="fas fa-angle-double-left"></i>
            </button>
            <button 
              @click="goToPage(currentPage - 1)" 
              class="btn-pagination" 
              :disabled="currentPage === 1"
            >
              <i class="fas fa-angle-left"></i>
            </button>
            <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
            <button 
              @click="goToPage(currentPage + 1)" 
              class="btn-pagination" 
              :disabled="currentPage === totalPages"
            >
              <i class="fas fa-angle-right"></i>
            </button>
            <button 
              @click="goToPage(totalPages)" 
              class="btn-pagination" 
              :disabled="currentPage === totalPages"
            >
              <i class="fas fa-angle-double-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Invite User Modal -->
    <div v-if="showInviteModal" class="modal-overlay" @click="closeInviteModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3><i class="fas fa-user-plus"></i> Invite New User</h3>
          <button @click="closeInviteModal" class="btn-close">×</button>
        </div>
        
        <div class="modal-body">
          <form @submit.prevent="inviteUser" class="invite-form">
            <div class="form-row">
              <div class="form-group">
                <label>First Name *</label>
                <input 
                  v-model="newUser.first_name" 
                  class="form-control" 
                  required
                  placeholder="John"
                >
              </div>
              <div class="form-group">
                <label>Last Name *</label>
                <input 
                  v-model="newUser.last_name" 
                  class="form-control" 
                  required
                  placeholder="Doe"
                >
              </div>
            </div>
            
            <div class="form-group">
              <label>Email Address *</label>
              <input 
                v-model="newUser.email" 
                type="email" 
                class="form-control" 
                required
                placeholder="john.doe@company.com"
              >
            </div>
            
            <div class="form-group">
              <label>Role *</label>
              <select v-model="newUser.role" class="form-control" required>
                <option value="">Select Role</option>
                <option v-for="role in invitableRoles" :key="role.value" :value="role.value">
                  {{ role.label }}
                </option>
              </select>
              <small v-if="newUser.role && roleDefinitions[newUser.role]" class="role-description">
                {{ roleDefinitions[newUser.role].description }}
              </small>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>Title</label>
                <input 
                  v-model="newUser.title" 
                  class="form-control" 
                  placeholder="Compliance Manager"
                >
              </div>
              <div class="form-group">
                <label>Department</label>
                <input 
                  v-model="newUser.department" 
                  class="form-control" 
                  placeholder="Risk Management"
                >
              </div>
            </div>
            
            <div class="form-group" v-if="showOrgAdminCheckbox">
              <label>
                <input 
                  v-model="newUser.is_organization_admin" 
                  type="checkbox"
                  class="checkbox"
                > 
                Grant Organization Admin Access
              </label>
              <small class="form-help">Allow this user to manage other users and organization settings (in addition to their role permissions)</small>
            </div>
          </form>
        </div>
        
        <div class="modal-footer">
          <button @click="closeInviteModal" class="btn btn-secondary">Cancel</button>
          <button 
            @click="inviteUser" 
            class="btn btn-primary" 
            :disabled="inviting || !isInviteFormValid"
          >
            <span v-if="inviting" class="spinner"></span>
            {{ inviting ? 'Sending Invitation...' : 'Send Invitation' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Edit User Modal -->
    <div v-if="editingUser" class="modal-overlay" @click="closeEditModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3><i class="fas fa-user-edit"></i> Edit User</h3>
          <button @click="closeEditModal" class="btn-close">×</button>
        </div>
        
        <div class="modal-body">
          <form @submit.prevent="updateUser" class="edit-form">
            <div class="form-row">
              <div class="form-group">
                <label>First Name *</label>
                <input 
                  v-model="editingUser.first_name" 
                  class="form-control" 
                  required
                >
              </div>
              <div class="form-group">
                <label>Last Name *</label>
                <input 
                  v-model="editingUser.last_name" 
                  class="form-control" 
                  required
                >
              </div>
            </div>
            
            <div class="form-group">
              <label>Email Address *</label>
              <input 
                v-model="editingUser.email" 
                type="email" 
                class="form-control" 
                required
                :disabled="!canEditUserEmail()"
              >
            </div>
            
            <div class="form-group">
              <label>Role *</label>
              <select v-model="editingUser.role" class="form-control" required>
                <option v-for="role in availableRoles" :key="role.value" :value="role.value">
                  {{ role.label }}
                </option>
              </select>
              <small v-if="editingUser.role && roleDefinitions[editingUser.role]" class="role-description">
                {{ roleDefinitions[editingUser.role].description }}
              </small>
            </div>
            
            <div class="form-row">
              <div class="form-group">
                <label>Title</label>
                <input 
                  v-model="editingUser.title" 
                  class="form-control"
                >
              </div>
              <div class="form-group">
                <label>Department</label>
                <input 
                  v-model="editingUser.department" 
                  class="form-control"
                >
              </div>
            </div>
            
            <div class="form-group">
              <label>
                <input 
                  v-model="editingUser.is_organization_admin" 
                  type="checkbox"
                  class="checkbox"
                  :disabled="!canEditUserPermissions()"
                > 
                Organization Administrator
              </label>
              <small class="form-help">User can manage other users and organization settings</small>
            </div>
          </form>
        </div>
        
        <div class="modal-footer">
          <button @click="closeEditModal" class="btn btn-secondary">Cancel</button>
          <button 
            @click="updateUser" 
            class="btn btn-primary" 
            :disabled="updating"
          >
            <span v-if="updating" class="spinner"></span>
            {{ updating ? 'Updating...' : 'Update User' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Notifications -->
    <div v-if="notification" class="notification" :class="notification.type">
      <i :class="getNotificationIcon(notification.type)"></i>
      {{ notification.message }}
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import auth from '@/services/auth'

export default {
  name: 'UserManagement',
  data() {
    return {
      loading: false,
      inviting: false,
      updating: false,
      users: [],
      organization: null,
      availableRoles: [],
      
      // Role definitions with descriptions
      roleDefinitions: {
        // Platform Roles (only shown to platform users)
        'PLATFORM_ADMIN': {
          label: 'Platform Admin',
          description: 'Full platform access including all organizations, billing, data import, compliance configuration, and all proprietary features.',
          tier: 'platform',
          color: '#9b59b6'
        },
        'PLATFORM_ENGINEER': {
          label: 'Platform Engineer',
          description: 'Technical platform access for development, debugging, integrations, and technical tools. No billing management.',
          tier: 'platform',
          color: '#8e44ad'
        },
        // Client Admin Roles
        'ORG_ADMIN': {
          label: 'Organization Admin',
          description: 'Full organization access. Manage users, settings, billing, and branding. Access all modules but cannot configure compliance checks.',
          tier: 'admin',
          color: '#3498db'
        },
        'LMS_ADMIN': {
          label: 'LMS Admin',
          description: 'Learning Management administrator. Create/manage courses, learning paths, and learners. Access LMS analytics.',
          tier: 'admin',
          color: '#2980b9'
        },
        'COMPLIANCE_ADMIN': {
          label: 'Compliance Admin',
          description: 'Compliance administrator. Manage subjects, run reports, make decisions. Cannot configure compliance checks.',
          tier: 'admin',
          color: '#1abc9c'
        },
        // Client User Roles
        'ANALYST': {
          label: 'Analyst',
          description: 'Compliance analyst. Work with subjects, view reports, and act on gaps in Compliance Health.',
          tier: 'user',
          color: '#27ae60'
        },
        'LEARNER': {
          label: 'Learner',
          description: 'Learning user. Browse courses, complete training, track progress, earn certifications.',
          tier: 'user',
          color: '#f39c12'
        },
        'VIEWER': {
          label: 'Viewer',
          description: 'Read-only access. View dashboards, reports, and Compliance Health without making changes.',
          tier: 'user',
          color: '#95a5a6'
        },
        // Deprecated Roles (shown for existing users but not for new assignments)
        'ADMIN': {
          label: 'Admin (Legacy)',
          description: '[Migrating to Organization Admin] Full organization access.',
          tier: 'deprecated',
          color: '#7f8c8d'
        },
        'RISK_MANAGER': {
          label: 'Risk Manager (Legacy)',
          description: '[Migrating to Compliance Admin] Compliance administrator.',
          tier: 'deprecated',
          color: '#7f8c8d'
        },
        'CLIENT_MANAGER': {
          label: 'Client Manager (Legacy)',
          description: '[Migrating to Analyst] Compliance analyst.',
          tier: 'deprecated',
          color: '#7f8c8d'
        },
        'REVIEWER': {
          label: 'Reviewer (Legacy)',
          description: '[Migrating to Viewer] Read-only access.',
          tier: 'deprecated',
          color: '#7f8c8d'
        },
        'READ_ONLY': {
          label: 'Read Only (Legacy)',
          description: '[Migrating to Viewer] Read-only access.',
          tier: 'deprecated',
          color: '#7f8c8d'
        },
        'STUDENT': {
          label: 'Non-Member',
          description: 'Non-member user with limited access.',
          tier: 'deprecated',
          color: '#7f8c8d'
        }
      },
      
      // Search and filters
      searchQuery: '',
      roleFilter: '',
      statusFilter: '',
      sortField: 'first_name',
      sortDirection: 'asc',
      
      // Pagination
      currentPage: 1,
      pageSize: 20,
      
      // Modals
      showInviteModal: false,
      editingUser: null,
      
      // Forms
      newUser: {
        first_name: '',
        last_name: '',
        email: '',
        role: '',
        title: '',
        department: '',
        is_organization_admin: false
      },
      
      // Notifications
      notification: null,
    }
  },
  
  computed: {
    filteredUsers() {
      let filtered = this.users;
      
      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(user => 
          user.first_name.toLowerCase().includes(query) ||
          user.last_name.toLowerCase().includes(query) ||
          user.email.toLowerCase().includes(query) ||
          this.getRoleLabel(user.role).toLowerCase().includes(query) ||
          (user.department && user.department.toLowerCase().includes(query))
        );
      }
      
      // Role filter
      if (this.roleFilter) {
        filtered = filtered.filter(user => user.role === this.roleFilter);
      }
      
      // Status filter
      if (this.statusFilter) {
        const isActive = this.statusFilter === 'active';
        filtered = filtered.filter(user => user.is_active === isActive);
      }
      
      // Sort
      filtered.sort((a, b) => {
        let aVal = a[this.sortField];
        let bVal = b[this.sortField];
        
        if (this.sortField === 'last_active_at') {
          aVal = new Date(aVal || 0);
          bVal = new Date(bVal || 0);
        } else if (typeof aVal === 'string') {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }
        
        if (aVal < bVal) return this.sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
      
      return filtered;
    },
    
    paginatedUsers() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredUsers.slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.filteredUsers.length / this.pageSize);
    },
    
    canInviteUsers() {
      return true; // TODO: Check permissions from current user context
    },
    
    invitableRoles() {
      // Filter out platform roles for customer organizations and deprecated roles
      const deprecated = ['ADMIN', 'RISK_MANAGER', 'CLIENT_MANAGER', 'REVIEWER', 'READ_ONLY', 'STUDENT'];
      return this.availableRoles.filter(role => 
        !role.value.startsWith('PLATFORM_') && !deprecated.includes(role.value)
      );
    },
    
    showOrgAdminCheckbox() {
      // Only show for non-admin roles (admin roles already have management access)
      const adminRoles = ['ORG_ADMIN', 'PLATFORM_ADMIN', 'PLATFORM_ENGINEER'];
      return this.newUser.role && !adminRoles.includes(this.newUser.role);
    },
    
    isInviteFormValid() {
      return this.newUser.first_name && 
             this.newUser.last_name && 
             this.newUser.email && 
             this.newUser.role;
    }
  },
  
  async mounted() {
    await this.loadInitialData();
  },
  
  methods: {
    async loadInitialData() {
      await Promise.all([
        this.loadUsers(),
        this.loadRoles(),
        this.loadOrganization()
      ]);
    },
    
    async loadUsers() {
      this.loading = true;
      try {
        const response = await api.get('/users/organization/');
        this.users = response.data;
      } catch (error) {
        this.showNotification('error', 'Failed to load users');
        console.error('Load users error:', error);
      } finally {
        this.loading = false;
      }
    },
    
    async loadRoles() {
      try {
        const response = await api.get('/users/roles/');
        this.availableRoles = response.data;
      } catch (error) {
        console.error('Load roles error:', error);
        // Fallback roles - use new role structure
        this.availableRoles = this.getDefaultRoles();
      }
    },
    
    getDefaultRoles() {
      // Build role list based on current user's permissions
      const roles = [];
      
      // Platform roles (only for platform users)
      if (auth.hasPlatformAccess()) {
        roles.push(
          { value: 'PLATFORM_ADMIN', label: 'Platform Admin', description: this.roleDefinitions['PLATFORM_ADMIN'].description },
          { value: 'PLATFORM_ENGINEER', label: 'Platform Engineer', description: this.roleDefinitions['PLATFORM_ENGINEER'].description }
        );
      }
      
      // Client Admin roles
      roles.push(
        { value: 'ORG_ADMIN', label: 'Organization Admin', description: this.roleDefinitions['ORG_ADMIN'].description },
        { value: 'LMS_ADMIN', label: 'LMS Admin', description: this.roleDefinitions['LMS_ADMIN'].description },
        { value: 'COMPLIANCE_ADMIN', label: 'Compliance Admin', description: this.roleDefinitions['COMPLIANCE_ADMIN'].description }
      );
      
      // Client User roles
      roles.push(
        { value: 'ANALYST', label: 'Analyst', description: this.roleDefinitions['ANALYST'].description },
        { value: 'LEARNER', label: 'Learner', description: this.roleDefinitions['LEARNER'].description },
        { value: 'VIEWER', label: 'Viewer', description: this.roleDefinitions['VIEWER'].description }
      );
      
      return roles;
    },
    
    async loadOrganization() {
      try {
        const response = await api.get('/users/organization/info/');
        this.organization = response.data;
      } catch (error) {
        console.error('Load organization error:', error);
      }
    },
    
    async inviteUser() {
      this.inviting = true;
      try {
        await api.post('/users/invite/', this.newUser);
        this.showNotification('success', `Invitation sent to ${this.newUser.email}`);
        this.closeInviteModal();
        await this.loadUsers();
      } catch (error) {
        this.showNotification('error', error.response?.data?.detail || 'Failed to send invitation');
      } finally {
        this.inviting = false;
      }
    },
    
    async updateUser() {
      this.updating = true;
      try {
        await api.put(`/users/${this.editingUser.id}/`, this.editingUser);
        this.showNotification('success', 'User updated successfully');
        this.closeEditModal();
        await this.loadUsers();
      } catch (error) {
        this.showNotification('error', error.response?.data?.detail || 'Failed to update user');
      } finally {
        this.updating = false;
      }
    },
    
    async toggleUserStatus(user) {
      const action = user.is_active ? 'deactivate' : 'activate';
      if (!confirm(`Are you sure you want to ${action} ${user.first_name} ${user.last_name}?`)) return;
      
      try {
        await api.patch(`/users/${user.id}/`, { is_active: !user.is_active });
        this.showNotification('success', `User ${action}d successfully`);
        await this.loadUsers();
      } catch (error) {
        this.showNotification('error', `Failed to ${action} user`);
      }
    },
    
    editUser(user) {
      this.editingUser = { ...user };
    },
    
    closeInviteModal() {
      this.showInviteModal = false;
      this.newUser = {
        first_name: '',
        last_name: '',
        email: '',
        role: '',
        title: '',
        department: '',
        is_organization_admin: false
      };
    },
    
    closeEditModal() {
      this.editingUser = null;
    },
    
    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortDirection = 'asc';
      }
    },
    
    getSortIcon(field) {
      if (this.sortField !== field) return 'fas fa-sort';
      return this.sortDirection === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
    },
    
    goToPage(page) {
      this.currentPage = Math.max(1, Math.min(page, this.totalPages));
    },
    
    getInitials(firstName, lastName) {
      return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
    },
    
    getRoleLabel(role) {
      const def = this.roleDefinitions[role];
      return def ? def.label : role;
    },
    
    getRoleDescription(role) {
      const def = this.roleDefinitions[role];
      return def ? def.description : 'Unknown role';
    },
    
    getRoleClass(role) {
      const classMap = {
        // Platform Roles
        'PLATFORM_ADMIN': 'platform-admin',
        'PLATFORM_ENGINEER': 'platform-engineer',
        // Client Admin Roles
        'ORG_ADMIN': 'org-admin',
        'LMS_ADMIN': 'lms-admin',
        'COMPLIANCE_ADMIN': 'compliance-admin',
        // Client User Roles
        'ANALYST': 'analyst',
        'LEARNER': 'learner',
        'VIEWER': 'viewer',
        // Deprecated Roles
        'ADMIN': 'deprecated',
        'RISK_MANAGER': 'deprecated',
        'CLIENT_MANAGER': 'deprecated',
        'REVIEWER': 'deprecated',
        'READ_ONLY': 'deprecated',
        'STUDENT': 'deprecated'
      };
      return classMap[role] || 'default';
    },
    
    isDeprecatedRole(role) {
      const deprecated = ['ADMIN', 'RISK_MANAGER', 'CLIENT_MANAGER', 'REVIEWER', 'READ_ONLY', 'STUDENT'];
      return deprecated.includes(role);
    },
    
    formatDate(dateString) {
      if (!dateString) return 'Never';
      return new Date(dateString).toLocaleDateString();
    },
    
    canEditUser() {
      // TODO: Implement proper permission checking based on current user role
      return true;
    },
    
    canEditUserEmail() {
      // Usually email changes require special handling
      return false;
    },
    
    canEditUserPermissions() {
      // TODO: Check if current user can edit this user's permissions
      return true;
    },
    
    showNotification(type, message) {
      this.notification = { type, message };
      setTimeout(() => {
        this.notification = null;
      }, 5000);
    },
    
    getNotificationIcon(type) {
      const icons = {
        success: 'fas fa-check-circle',
        error: 'fas fa-exclamation-circle',
        warning: 'fas fa-exclamation-triangle',
        info: 'fas fa-info-circle'
      };
      return icons[type] || icons.info;
    }
  }
}
</script>

<style scoped>
.user-management {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

/* Header */
.header-section {
  margin-bottom: 30px;
  text-align: center;
}

.header-section h2 {
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 2.2rem;
}

.description {
  color: #7f8c8d;
  font-size: 16px;
  max-width: 800px;
  margin: 0 auto;
}

/* Management Section */
.management-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
}

.controls-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
}

.section-info h3 {
  color: white;
  margin: 0 0 5px 0;
  font-size: 1.8rem;
}

.section-description {
  color: rgba(255,255,255,0.9);
  margin: 0;
  font-size: 14px;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

/* Filters */
.filters-section {
  display: flex;
  gap: 15px;
  margin-bottom: 25px;
  background: rgba(255,255,255,0.95);
  padding: 20px;
  border-radius: 12px;
  backdrop-filter: blur(10px);
}

.search-group {
  position: relative;
  flex: 1;
  max-width: 400px;
}

.search-input {
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
}

.search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #7f8c8d;
}

.filter-select {
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  min-width: 150px;
}

/* Users Table */
.users-table-container {
  background: rgba(255,255,255,0.95);
  border-radius: 12px;
  overflow: hidden;
  backdrop-filter: blur(10px);
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
}

.modern-table th {
  background: #f8f9fa;
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 1px solid #e9ecef;
}

.modern-table th.sortable {
  cursor: pointer;
  user-select: none;
}

.modern-table th.sortable:hover {
  background: #e9ecef;
}

.modern-table td {
  padding: 16px;
  border-bottom: 1px solid #f1f3f4;
}

.user-row:hover {
  background: #f8f9fa;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.user-name {
  font-weight: 600;
  color: #2c3e50;
}

.user-title {
  font-size: 12px;
  color: #7f8c8d;
}

/* Badges */
.role-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

/* Platform Roles */
.role-badge.platform-admin { background: #f3e5f5; color: #9b59b6; border: 1px solid #9b59b6; }
.role-badge.platform-engineer { background: #ede7f6; color: #8e44ad; border: 1px solid #8e44ad; }

/* Client Admin Roles */
.role-badge.org-admin { background: #e3f2fd; color: #3498db; }
.role-badge.lms-admin { background: #e1f5fe; color: #2980b9; }
.role-badge.compliance-admin { background: #e0f2f1; color: #1abc9c; }

/* Client User Roles */
.role-badge.analyst { background: #e8f5e9; color: #27ae60; }
.role-badge.learner { background: #fff8e1; color: #f39c12; }
.role-badge.viewer { background: #eceff1; color: #607d8b; }

/* Deprecated Roles */
.role-badge.deprecated { 
  background: #fafafa; 
  color: #9e9e9e; 
  border: 1px dashed #bdbdbd;
  font-style: italic;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.active { background: #d4edda; color: #155724; }
.status-badge.inactive { background: #f8d7da; color: #721c24; }

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 8px;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: #f8f9fa;
  color: #6c757d;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-icon:hover:not(:disabled) {
  background: #e9ecef;
  color: #495057;
}

.btn-icon:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Pagination */
.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.pagination-info {
  color: #6c757d;
  font-size: 14px;
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-pagination {
  width: 32px;
  height: 32px;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-pagination:hover:not(:disabled) {
  background: #e9ecef;
  border-color: #adb5bd;
}

.btn-pagination:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  margin: 0 12px;
  font-size: 14px;
  color: #495057;
}

/* States */
.loading-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.loading-state i {
  font-size: 2rem;
  color: #6c757d;
  margin-bottom: 16px;
}

.empty-state i {
  font-size: 3rem;
  color: #6c757d;
  margin-bottom: 20px;
}

.empty-state h3 {
  color: #495057;
  margin-bottom: 8px;
}

.empty-state p {
  color: #6c757d;
  margin-bottom: 20px;
}

/* Modals */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h3 {
  margin: 0;
  color: #2c3e50;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6c757d;
  padding: 4px;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 20px;
  border-top: 1px solid #e9ecef;
}

/* Forms */
.form-row {
  display: flex;
  gap: 16px;
}

.form-group {
  flex: 1;
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  color: #2c3e50;
}

.form-control {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.checkbox {
  width: auto;
  margin-right: 8px;
}

.form-help {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #6c757d;
}

.role-description {
  display: block;
  margin-top: 8px;
  padding: 10px 12px;
  font-size: 12px;
  color: #495057;
  background: #f8f9fa;
  border-left: 3px solid #667eea;
  border-radius: 0 4px 4px 0;
  line-height: 1.5;
}

/* Buttons */
.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5a6fd8;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #5a6268;
}

.btn-outline {
  background: transparent;
  color: white;
  border: 1px solid rgba(255,255,255,0.5);
}

.btn-outline:hover:not(:disabled) {
  background: rgba(255,255,255,0.1);
  border-color: white;
}

/* Spinner */
.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Notifications */
.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 16px 20px;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  z-index: 1001;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 400px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}

.notification.success { background: #28a745; }
.notification.error { background: #dc3545; }
.notification.warning { background: #ffc107; color: #212529; }
.notification.info { background: #17a2b8; }

/* Responsive */
@media (max-width: 768px) {
  .user-management {
    padding: 15px;
  }
  
  .management-section {
    padding: 20px;
  }
  
  .controls-header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .filters-section {
    flex-direction: column;
    gap: 12px;
  }
  
  .form-row {
    flex-direction: column;
  }
  
  .pagination-controls {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }
  
  .action-buttons {
    justify-content: center;
  }
}
</style>